'use client';

import * as React from 'react';
import { Box } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';

export interface SetupGuardProps {
  children: React.ReactNode;
  inverse?: boolean;
}

export function SetupGuard({ children, inverse = false }: SetupGuardProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const { user, account, community, error, isLoading } = useUser();
  const [isChecking, setIsChecking] = React.useState<boolean>(true);
  const hasChecked = React.useRef<boolean>(false);

  const checkPermissions = async (): Promise<void> => {
    if (isLoading || hasChecked.current) {
      return;
    }

    if (error) {
      setIsChecking(false);
      return;
    }

    if (inverse) {
      if (account && user?.selected_account_id && (user?.selected_community_id || account?.setup_complete)) {
        navigate(paths.home, { replace: true });
        return;
      }
    } else if (
      user?.selected_account_id === null ||
      (user?.selected_community_id === null && !account?.setup_complete)
    ) {
      navigate(paths.setup.community, { replace: true });
      return;
    }

    setIsChecking(false);
    hasChecked.current = true;
  };

  React.useEffect(() => {
    checkPermissions().catch(() => {
      // noop
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
  }, [user, error, isLoading]);

  if (isChecking) {
    return null;
  }

  if (error) {
    return <Alert color="error">{error}</Alert>;
  }

  if (!account || !user?.selected_account_id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">No account found</Alert>
      </Box>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
}
